import { createStore } from "vuex"
import router, { reloadRoutes } from "@/router"
import { getInfo, getRouters, getPrincipal, logout } from "@/api/auth"
import { assembleRouterList } from "@/utils/router"
import { clearSSOPending, isAuthStateValid } from "@/utils/login"
import { AUTH_STATE_KEY, ROUTER_MAP_KEY } from '@/utils/constants'
import {
  setLocalItem,
  getLocalItem,
  setLocalJSON,
  getLocalJSON,
  removeLocalItem
} from "@/utils/storage"

// Helper to persist simple string values in localStorage when available.
const getPersisted = (key, fallback) => getLocalItem(key, fallback)
const persist = (key, value) => setLocalItem(key, value)

// Returns the cached principal data if AUTH_STATE_KEY holds a still-valid auth state, otherwise null
const getCachedPrincipalState = () => {
  const cachedAuthState = getLocalJSON(AUTH_STATE_KEY)
  if (isAuthStateValid(cachedAuthState)) {
    return cachedAuthState.data
  }
  return null
}

// Clears the persisted principal cache, used both when a fetch confirms "not authenticated" and on logout
const clearAuthCache = () => {
  removeLocalItem(AUTH_STATE_KEY)
}

// Helper to fetch principal from backend and update state
const fetchAndUpdatePrincipal = async (commit) => {
  console.log("[auth] Fetch principal state from backend")
  try {
    const principalResponse = await getPrincipal()
    const principalState = principalResponse.data
    const isAuthenticated = !!principalState?.authenticated
    if (isAuthenticated) {
      commit("SET_PRINCIPAL_STATE", principalState)
      setLocalJSON(AUTH_STATE_KEY, {
        authenticated: true,
        timestamp: Date.now(),
        data: principalState,
      })
    } else {
      clearAuthCache()
    }
    return isAuthenticated
  } catch (error) {
    console.warn("[auth] Failed to fetch principal", error)
    return false
  }
}

const store = createStore({
  state: () => ({
    preferredUsername: "not-set",
    username: "",
    nickName: "",
    roleMap: {},
    permissionMap: {},
    dict: {},
    serviceVersion: "",
    defaultRegion: "",
    defaultBrand: "",
    routerMap: getLocalJSON(ROUTER_MAP_KEY, {}),
    regionItems: [],
    brandItemsMap: {},
    brand: getPersisted("brand", ""),
    region: getPersisted("region", ""),
    principalState: null,
    cachedRoutes: []
  }),
  mutations: {
    SET_USER_NAME(state, val) {
      state.username = val || ''
    },
    SET_NICK_NAME(state, val) {
      state.nickName = val || ''
    },
    SET_ROLE_MAP(state, val) {
      state.roleMap = val || {}
    },
    SET_PERMISSION_MAP(state, val) {
      state.permissionMap = val || {}
    },
    SET_DICT(state, val) {
      state.dict = val || {}
    },
    SET_SERVICE_VERSION(state, val) {
      state.serviceVersion = val || ''
    },
    SET_BRAND_MAP(state, val) {
      state.brandMap = val || {}
    },
    SET_DEFAULT_REGION(state, val) {
      state.defaultRegion = val || ''
    },
    SET_DEFAULT_BRAND(state, val) {
      state.defaultBrand = val || ''
    },
    SET_REGION_ITEMS(state, val) {
      state.regionItems = val || []
    },
    SET_BRAND_ITEMS_MAP(state, val) {
      state.brandItemsMap = val || {}
    },
    SET_ROUTER_MAP: (state, routerMap) => {
      state.routerMap = routerMap
      setLocalJSON(ROUTER_MAP_KEY, routerMap)
    },
    SET_PRINCIPAL_STATE(state, payload) {
      state.principalState = payload
    },
    SET_PREFERRED_USERNAME(state, payload) {
      state.preferredUsername = payload || "not-set"
    },
    SET_SELECTED_REGION(state, value) {
      state.region = value || ""
      persist("region", state.region)
      console.log(`[switch] Set region to ${state.region}`)
    },
    SET_SELECTED_BRAND(state, value) {
      state.brand = value || ""
      persist("brand", state.brand)
      console.log(`[switch] Set brand to ${state.brand}`)
      // TODO fix issue that UI didn't refresh
    },
    SET_CACHED_ROUTES(state, routes) {
      state.cachedRoutes = routes || []
    },
  },
  actions: {

    // Get user information
    GetInfo({ commit, state }) {
      // TODO load once in cache in future
      return new Promise((resolve, reject) => {
        getInfo()
          .then((res) => {
            commit("SET_ROLE_MAP", res.roleMap)
            commit("SET_PERMISSION_MAP", res.permissionMap)
            commit("SET_DICT", res.dict)
            commit("SET_USER_NAME", res.user?.userName)
            commit("SET_NICK_NAME", res.user?.nickName)
            commit("SET_SERVICE_VERSION", res.serviceVersion)

            // Hide NAR from selection map if feature flag off
            if (!res.narEnabled) {
              const narKey = "NAR"
              if (res.brandMap && narKey in res.brandMap) {
                console.warn("Disable NAR brands as feature flag is off")
                delete res.brandMap[narKey]
              }
            }
            commit("SET_BRAND_MAP", res.brandMap)

            // If no brand/region permission
            if (!res.brandMap || Object.keys(res.brandMap).length === 0) {
              console.warn("User have no region and brand related permission")
              resolve(res)
              return
            }

            const brandMap = res.brandMap
            const defaultRegionName = Object.keys(brandMap)[0]
            const defaultBrandName = brandMap[defaultRegionName]?.[0]

            const regionTextDict = res.dict?.regionText || {}
            const brandTextDict = res.dict?.brandText || {}
            const regionItems = []
            const brandItemsMap = {}
            Object.keys(brandMap).forEach((regionName) => {
              regionItems.push({ text: regionTextDict[regionName], value: regionName })
              const brandItems = []
              if (brandMap[regionName]) {
                brandMap[regionName].forEach((brandName) => {
                  brandItems.push({ text: brandTextDict[brandName], value: brandName })
                })
              }
              brandItemsMap[regionName] = brandItems
            })
            commit("SET_REGION_ITEMS", regionItems)
            commit("SET_BRAND_ITEMS_MAP", brandItemsMap)

            commit("SET_DEFAULT_REGION", defaultRegionName)
            commit("SET_DEFAULT_BRAND", defaultBrandName)

            // Apply defaults to the active selection so API headers include them
            commit("SET_SELECTED_REGION", defaultRegionName)
            commit("SET_SELECTED_BRAND", defaultBrandName)

            resolve(res)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    async GetPrincipal({ commit, state }) {
      const cachedPrincipal = getCachedPrincipalState()

      // Use memory cache if it exists and localStorage cache hasn't expired for better performance
      if (cachedPrincipal) {
        // Avoid re-committing the same data when memory already holds it, to preserve object reference
        if (!state.principalState) {
          commit("SET_PRINCIPAL_STATE", cachedPrincipal)
        }
        console.log("[auth] Retrieve valid principal state from cache")
        return true
      }

      // If cache miss or expired, fetch principal state from backend
      return await fetchAndUpdatePrincipal(commit)
    },

    async RefreshPrincipal({ commit }) {
      return await fetchAndUpdatePrincipal(commit)
    },

    GetRouters({ commit, state }) {
      return new Promise((resolve, reject) => {
        // Check if routerMap already exists in local storage
        if (state.routerMap && Object.keys(state.routerMap).length > 0) {
          console.debug("[store] Using cached routerMap from local storage")
          resolve({ data: state.routerMap })
          return
        }

        // Fetch from backend if not in cache
        console.debug("[store] Fetching routerMap from backend")
        getRouters()
          .then((res) => {
            const routerMap = JSON.parse(JSON.stringify(res.data))
            commit("SET_ROUTER_MAP", routerMap)
            resolve(res)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    async GenerateRoutes({ commit, state }) {
      console.log("Generating routes triggered")
      // Apply default region and brand if not selected
      // TODO make sure the state load from cache and restore the last selection
      const regionName = state.region ? state.region : state.defaultRegion
      const brandName = state.brand ? state.brand : state.defaultBrand

      const routerList = assembleRouterList(
        state.routerMap,
        state.brandMap,
        regionName,
        brandName
      )

      reloadRoutes(routerList)
      commit('SET_CACHED_ROUTES', router.getRoutes())
    },

    LogOut({ commit }) {
      return new Promise((resolve, reject) => {
        clearSSOPending()
        clearAuthCache()
        removeLocalItem(ROUTER_MAP_KEY)
        // TODO consider run sessionStorage.clear() and localStorage.clear() 
        commit("SET_ROUTER_MAP", {})
        commit("SET_PRINCIPAL_STATE", null)
        commit("SET_SELECTED_REGION", null)
        commit("SET_SELECTED_BRAND", null)

        logout()
          .then(() => {
            console.log("Logout request successful")
            resolve()
          })
          .catch((error) => {
            reject(error)
          })
      })
    },
  },
  getters: {
    preferredUsername: (state) => state.preferredUsername,
    username: (state) => state.username,
    nickName: (state) => state.nickName,
    roleMap: (state) => state.roleMap,
    permissionMap: (state) => state.permissionMap,
    dict: (state) => state.dict,
    serviceVersion: (state) => state.serviceVersion,
    defaultRegion: (state) => state.defaultRegion,
    defaultBrand: (state) => state.defaultBrand,
    regionItems: (state) => state.regionItems,
    brandItemsMap: (state) => state.brandItemsMap,
    brand: (state) => state.brand,
    region: (state) => state.region,
    principalState: (state) => state.principalState,
    cachedRoutes: (state) => state.cachedRoutes,
  },
})

export default store
